import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Animated, Dimensions, Easing, Platform, StyleSheet, View} from 'react-native';
import {useManagementTheme} from '../../helpers/managementTheme';

const {width: SCREEN_W, height: SCREEN_H} = Dimensions.get('window');
const IS_WEB = Platform.OS === 'web';

const hexToRgba = (hex, alpha) => {
    const h = String(hex || '#F1B34B').replace('#', '');
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * A soft, gradient, blurred orange splash.
 *  - On web/desktop it is a real CSS radial-gradient + blur() glow that smoothly
 *    follows the cursor — exactly like the hero splash on happysolutions.hu.
 *  - On touch devices it is a smooth symmetric radial glow (stacked translucent
 *    circles, no native blur dependency) that drifts slowly around the screen.
 */
function AnimatedGradientBackground({intensity}) {
    const m = useManagementTheme();

    const baseOpacity = intensity ?? (m.isDark ? 0.38 : 0.43);
    const size = Math.min(SCREEN_W * 1.2, IS_WEB ? 820 : SCREEN_W * 1.2);

    // Native / idle drift driver (0 -> 1 looping)
    const progress = useRef(new Animated.Value(0)).current;
    // Web cursor-follow position (top-left of the circle)
    const follow = useRef(new Animated.ValueXY({
        x: SCREEN_W / 2 - size / 2,
        y: SCREEN_H / 2 - size / 2,
    })).current;
    const containerRef = useRef(null);
    const glowRef = useRef(null);
    const idleAnimRef = useRef(null);
    const [following, setFollowing] = useState(false);

    const orange = m.colors.primary;

    // ---- Touch / idle slow drift ----------------------------------------
    useEffect(() => {
        const animation = Animated.loop(
            Animated.sequence([
                Animated.timing(progress, {toValue: 1, duration: 22000, easing: Easing.inOut(Easing.sin), useNativeDriver: !IS_WEB}),
                Animated.timing(progress, {toValue: 0, duration: 22000, easing: Easing.inOut(Easing.sin), useNativeDriver: !IS_WEB}),
            ]),
        );
        animation.start();
        return () => animation.stop();
    }, [progress]);

    // ---- Web: apply the real blurred radial gradient to the DOM node -----
    useEffect(() => {
        if (!IS_WEB) return;
        const node = glowRef.current;
        if (!node || !node.style) return;
        node.style.background = `radial-gradient(circle closest-side, ${hexToRgba(orange, 0.55)} 0%, ${hexToRgba(orange, 0.32)} 32%, ${hexToRgba(orange, 0.12)} 55%, ${hexToRgba(orange, 0)} 75%)`;
        node.style.filter = `blur(${Math.round(size * 0.09)}px)`;
        node.style.borderRadius = '50%';
        node.style.willChange = 'transform';
    }, [orange, size, m.isDark]);

    // ---- Web: follow the cursor -----------------------------------------
    useEffect(() => {
        if (!IS_WEB || typeof window === 'undefined') return undefined;

        let leaveTimer = null;

        const startIdleDrift = () => {
            if (idleAnimRef.current) return;
            const loop = Animated.loop(
                Animated.sequence([
                    Animated.timing(follow, {
                        toValue: {x: SCREEN_W * 0.28 - size / 2, y: SCREEN_H * 0.30 - size / 2},
                        duration: 12000, easing: Easing.inOut(Easing.sin), useNativeDriver: false,
                    }),
                    Animated.timing(follow, {
                        toValue: {x: SCREEN_W * 0.62 - size / 2, y: SCREEN_H * 0.55 - size / 2},
                        duration: 12000, easing: Easing.inOut(Easing.sin), useNativeDriver: false,
                    }),
                ]),
            );
            idleAnimRef.current = loop;
            loop.start();
        };

        const stopIdleDrift = () => {
            if (idleAnimRef.current) {
                idleAnimRef.current.stop();
                idleAnimRef.current = null;
            }
        };

        const handleMove = (event) => {
            if (leaveTimer) {
                globalThis.clearTimeout(leaveTimer);
                leaveTimer = null;
            }
            stopIdleDrift();
            setFollowing(true);

            let left = 0;
            let top = 0;
            try {
                const rect = containerRef.current?.getBoundingClientRect?.();
                if (rect) {
                    left = rect.left;
                    top = rect.top;
                }
            } catch (_) {
                // ignore
            }

            Animated.spring(follow, {
                toValue: {x: event.clientX - left - size / 2, y: event.clientY - top - size / 2},
                friction: 9,
                tension: 45,
                useNativeDriver: false,
            }).start();
        };

        const handleLeave = () => {
            leaveTimer = globalThis.setTimeout(() => {
                setFollowing(false);
                startIdleDrift();
            }, 400);
        };

        startIdleDrift();
        window.addEventListener('mousemove', handleMove, {passive: true});
        window.addEventListener('mouseout', handleLeave, {passive: true});

        return () => {
            if (leaveTimer) globalThis.clearTimeout(leaveTimer);
            stopIdleDrift();
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseout', handleLeave);
        };
    }, [follow, size]);

    // Smooth symmetric radial glow for native (stacked translucent circles)
    const layers = useMemo(() => Array.from({length: 16}, (_, i) => {
        const t = i / 15; // 0 (outer) -> 1 (inner)
        return {
            sizeFactor: 1 - t * 0.92,
            color: hexToRgba(orange, 0.03 + t * 0.16),
        };
    }), [orange]);

    // Native / idle transforms from the looping progress value. Memoized so the
    // native animated-node graph is built exactly once: recreating these
    // interpolation nodes on every render churns the native node map while the
    // native-driver loop is running, which on low-end devices races into
    // "connectAnimatedNodes: Animated node with tag (child) does not exist"
    // (NativeAnimatedNodesManager crash).
    const nativeTransform = useMemo(() => {
        const driftX = progress.interpolate({
            inputRange: [0, 0.5, 1],
            outputRange: [-SCREEN_W * 0.28, SCREEN_W * 0.30, -SCREEN_W * 0.28],
        });
        const driftY = progress.interpolate({
            inputRange: [0, 0.5, 1],
            outputRange: [-SCREEN_H * 0.06, SCREEN_H * 0.40, SCREEN_H * 0.70],
        });
        const scale = progress.interpolate({inputRange: [0, 0.5, 1], outputRange: [1, 1.1, 1]});
        return [{translateX: driftX}, {translateY: driftY}, {scale}];
    }, [progress]);

    const transform = IS_WEB
        ? [{translateX: follow.x}, {translateY: follow.y}, {scale: following ? 1.04 : 1}]
        : nativeTransform;

    return (
        <View ref={containerRef} pointerEvents="none" style={[StyleSheet.absoluteFill, {backgroundColor: m.colors.bg, overflow: 'hidden'}]}>
            <Animated.View
                ref={glowRef}
                style={[
                    styles.circle,
                    {
                        width: size,
                        height: size,
                        borderRadius: size / 2,
                        opacity: baseOpacity,
                        transform,
                    },
                ]}
            >
                {!IS_WEB && layers.map((layer, idx) => {
                    const ringSize = size * layer.sizeFactor;
                    return (
                        <View
                            key={idx}
                            style={{
                                position: 'absolute',
                                width: ringSize,
                                height: ringSize,
                                borderRadius: ringSize / 2,
                                backgroundColor: layer.color,
                            }}
                        />
                    );
                })}
            </Animated.View>
        </View>
    );
}

const styles = StyleSheet.create({
    circle: {position: 'absolute', alignItems: 'center', justifyContent: 'center'},
});

// Memoized: this background is a persistent native-driver animation mounted behind
// every management screen, so it must not re-render (and rebuild native animated
// nodes) every time the surrounding dashboard content updates.
export default React.memo(AnimatedGradientBackground);



